/**
 * `.scrollPosition(id:)` reading back (docs/ops-protocol.md, "Scrolling to a
 * view"): a `scrollview` with `reportsPosition` tells the app which scroll
 * target (`scrollID`) is at its anchor point once scrolling settles, as
 * SwiftUI sets the binding to the id of the view at the anchor. The renderer
 * gathers the targets' extents along the axis and asks `positionTarget`
 * which one is there; the event goes to that target's element.
 */

/** A scroll target's extent along the scroll axis, in the same coordinates as the viewport. */
export interface PositionCandidate {
  id: number;
  start: number;
  length: number;
}

/** A settled scroll is one with no `scroll` event for this long (`scrollend`, where the browser has it, ends the wait). */
export const POSITION_SETTLE_MS = 120;

/**
 * A snapping scroll view that settles off its snap points is between a
 * gesture and the browser's snap animation (a trackpad swipe ended, the
 * snap not yet begun): it waits this many settles more for the animation
 * to carry it onto one before reporting where it is.
 */
export const POSITION_SNAP_WAITS = 6;

/** How far from a snap point still counts as on it (subpixel scroll offsets). */
const SNAP_TOLERANCE = 1;

/**
 * Whether a snapping scroll view (`scrollTargetBehavior`) is at rest on a
 * snap point, so that the target at its anchor is the one the browser
 * chose rather than one passing by mid-snap: a target starts at the
 * snapport's start (`start` is the viewport's start plus its scroll
 * padding), or a target longer than the viewport covers the whole of it
 * (the browser lets the view rest anywhere such a target fills), or the
 * view is at either end of its range (the last snap point may be out of
 * reach).
 */
export function restsOnSnapPoint(candidates: readonly PositionCandidate[], start: number, viewportLength: number, offset: number, maxOffset: number): boolean {
  if (offset <= SNAP_TOLERANCE || offset >= maxOffset - SNAP_TOLERANCE) return true;
  for (const c of candidates) {
    if (c.length <= 0) continue;
    if (Math.abs(c.start - start) <= SNAP_TOLERANCE) return true;
    if (c.start <= start + SNAP_TOLERANCE && c.start + c.length >= start + viewportLength - SNAP_TOLERANCE && c.length > viewportLength + SNAP_TOLERANCE) return true;
  }
  return false;
}

/**
 * The target at `point` along the axis: the one whose extent holds the point
 * (the last of several, as a later sibling draws over an earlier one), else
 * the first one starting after it (the point is in a gap or before the
 * content), else the last one (the point is past the end). Null without
 * candidates; candidates of no extent are hidden and left out.
 */
export function positionTarget(candidates: readonly PositionCandidate[], point: number): number | null {
  const shown = candidates.filter((c) => c.length > 0);
  if (shown.length === 0) return null;
  let holding: PositionCandidate | null = null;
  let next: PositionCandidate | null = null;
  for (const c of shown) {
    if (c.start <= point && point < c.start + c.length) holding = c;
    else if (c.start > point && (!next || c.start < next.start)) next = c;
  }
  if (holding) return holding.id;
  if (next) return next.id;
  return shown.reduce((last, c) => (c.start >= last.start ? c : last)).id;
}

/** The anchor point of a viewport along the axis: its start plus the anchor's fraction of its length. */
export function anchorPoint(viewport: { start: number; length: number }, fraction: number): number {
  return viewport.start + Math.min(1, Math.max(0, fraction)) * viewport.length;
}
